import { Box, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import HandymanIcon from '@mui/icons-material/Handyman';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';

/**
 * Order-type chooser shown at the top of the New Warranty Ticket form.
 *
 * The selected type pre-fills the ticket's work status and decides which
 * sections of the form are emphasised. It does NOT hide sections — the tech
 * is still free to add parts to a Follow-up ticket, etc., we only nudge
 * toward the typical path for each type.
 *
 * Order types:
 *   - on-site   : work completed on-site, no parts ordering
 *   - small     : small part the tech will pick up themselves
 *                 (phone-ordered supplier is acceptable)
 *   - large     : large unit that ships to our warehouse and is installed
 *                 by an install crew. Strict path: defective model+serial
 *                 are required, supplier must have an email, and the
 *                 warehouse must be notified.
 *   - follow-up : a follow-up visit is needed; date/time/reason required.
 *
 * Default work status mapping (suggestion only — tech can still change):
 *   on-site   -> "Completed on-site"
 *   small     -> "Parts needed"   (will likely flip through ordered/received)
 *   large     -> "Parts needed"   (same, but with strict validation downstream)
 *   follow-up -> "Follow-up scheduled"
 */
export const ORDER_TYPES = [
  {
    id: 'on-site',
    icon: BuildIcon,
    title: 'Resolved on-site',
    subtitle: 'No parts ordered. Just close it.',
    color: '#2e7d32',
    workStatus: 'Completed on-site',
  },
  {
    id: 'small',
    icon: HandymanIcon,
    title: 'Small part — I will pick up myself',
    subtitle: 'OK to order by phone. No warehouse, no install crew.',
    color: '#0288d1',
    workStatus: 'Parts needed',
  },
  {
    id: 'large',
    icon: LocalShippingIcon,
    title: 'Large unit — ships to warehouse',
    subtitle: 'Email order to supplier + warehouse notified. Install crew will mount.',
    color: '#d32f2f',
    workStatus: 'Parts needed',
  },
  {
    id: 'follow-up',
    icon: EventRepeatIcon,
    title: 'Follow-up visit needed',
    subtitle: 'Schedule another visit.',
    color: '#ed6c02',
    workStatus: 'Follow-up scheduled',
  },
];

export function getOrderTypeById(id) {
  return ORDER_TYPES.find((t) => t.id === id) || null;
}

export default function WarrantyTicketOrderTypeSelector({ value, onChange, sx }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, ...sx }}>
      <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 700 }}>
        What kind of warranty work is this?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Pick one — the form will adjust and remind you of what is required for this scenario.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
          gap: 1.5,
        }}
      >
        {ORDER_TYPES.map((type) => {
          const Icon = type.icon;
          const selected = value === type.id;
          return (
            <Paper
              key={type.id}
              elevation={selected ? 3 : 0}
              onClick={() => onChange(type.id)}
              sx={{
                p: 1.5,
                cursor: 'pointer',
                border: '2px solid',
                borderColor: selected ? type.color : 'divider',
                bgcolor: selected ? `${type.color}10` : 'background.paper',
                transition: 'all 0.15s ease',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                '&:hover': {
                  borderColor: type.color,
                  bgcolor: `${type.color}08`,
                },
              }}
            >
              <Icon sx={{ color: type.color, fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: selected ? type.color : 'text.primary' }}
                >
                  {type.title}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.3 }}
                >
                  {type.subtitle}
                </Typography>
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Paper>
  );
}
